import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Link from '@mui/material/Link';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { postTaricSearch } from '../../api/taric';
import { Button } from '../../components/Buttons';
import ContentPanel from '../../components/ContentPanel';
import { RhfTextField } from '../../components/Inputs';
import AddCommodityCodeModal from './AddCommodityCodeModal';
import { taricSearchSchema } from './taricSearchSchema';

const TaricTool = () => {
  const [searchCode, setSearchCode] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const navigate = useNavigate();

  const { control, handleSubmit, getValues, setValue } = useForm({
    resolver: zodResolver(taricSearchSchema),
    defaultValues: {
      commodityCode: '',
    },
    mode: 'onSubmit',
  });

  const {
    data: results = [],
    error,
    isFetching,
    refetch,
  } = useQuery({
    queryKey: ['taricSearch', searchCode],
    queryFn: () => postTaricSearch(searchCode),
    enabled: !!searchCode,
  });

  const onSubmit = ({ commodityCode }) => {
    if (commodityCode === searchCode) {
      refetch();
    } else {
      setSearchCode(commodityCode);
    }
  };

  const handleResultClick = (code) => {
    navigate(`/taric-tool/${code}`);
  };

  const handleAddSuccess = () => {
    if (getValues('commodityCode')) {
      handleSubmit(onSubmit)();
    }
  };

  const handleCommodityCodeChange = (event) => {
    const value = event.target.value;
    if (/^\d*$/.test(value)) {
      setValue('commodityCode', value, { shouldValidate: true, shouldDirty: true });
    }
  };

  const searchError = error ? 'Failed to search. Please try again.' : '';
  const searched = !!searchCode;

  return (
    <div className="full-width-container">
      <ContentPanel maxWidth={500} sx={{ my: '2rem' }}>
        <Typography variant="h4" sx={{ color: '#033249', mb: 2, fontWeight: 500 }}>
          Taric Tool
        </Typography>

        <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 2,
              mb: 2,
            }}
          >
            <RhfTextField
              name="commodityCode"
              control={control}
              label="Commodity Code"
              autoComplete="off"
              disabled={isFetching}
              onChange={handleCommodityCodeChange}
              sx={{ flex: 1, minWidth: 0 }}
            />
            <Button
              type="submit"
              color="primary"
              disabled={isFetching}
              sx={{ minWidth: 100, flexShrink: 0, mt: '8px' }}
            >
              {isFetching ? 'Searching...' : 'Find Now'}
            </Button>
          </Box>

          <Divider sx={{ my: 2 }} />

          <Box sx={{ minHeight: 200 }}>
            {searchError && (
              <Typography color="error" fontWeight={500} sx={{ mb: 1 }}>
                {searchError}
              </Typography>
            )}
            {isFetching && (
              <Typography color="text.secondary" fontWeight={500} sx={{ mb: 1 }}>
                Searching...
              </Typography>
            )}
            {searched && !isFetching && results.length > 0 && (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Commodity Code</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {results.map((item, index) => {
                    const code = item.code || item.commodityCode || item.commCode;
                    return (
                      <TableRow key={`${code}-${index}`}>
                        <TableCell>
                          <Link
                            component="button"
                            type="button"
                            onClick={() => handleResultClick(code)}
                            sx={{ cursor: 'pointer' }}
                          >
                            {code}
                          </Link>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
            {searched && !isFetching && results.length === 0 && !searchError && (
              <Box sx={{ textAlign: 'center', py: 2.5 }}>
                <Typography color="text.secondary" fontWeight={500} sx={{ mb: 2 }}>
                  No results found.
                </Typography>
                <Button type="button" color="secondary" onClick={() => setShowAddModal(true)} sx={{ minWidth: 100 }}>
                  ADD
                </Button>
              </Box>
            )}
          </Box>
        </Box>
      </ContentPanel>

      <AddCommodityCodeModal open={showAddModal} onClose={() => setShowAddModal(false)} onSuccess={handleAddSuccess} />
    </div>
  );
};

export default TaricTool;
